import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { getIntl, history } from '@umijs/max';
import { ACTING_AS_STORAGE_KEY } from '@/models/actingAsMerchant';
import { message, notification } from '@/utils/antdStatic';
import { clearAuthState } from '@/utils/authState';

// Error display types
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}

// Response structure
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  message?: string;
  showType?: ErrorShowType;
}

// Token refresh state
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

// Subscribe to token refresh
const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

// Notify all subscribers with new token
const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach((callback) => {
    callback(token);
  });
  refreshSubscribers = [];
};

// Check if token is about to expire (within 5 minutes)
const isTokenExpiringSoon = (): boolean => {
  const expiresAt = localStorage.getItem('aimerpay_token_expires');
  if (!expiresAt) return false;

  const expiresTime = new Date(expiresAt).getTime();
  const now = Date.now();
  const fiveMinutes = 5 * 60 * 1000;

  return expiresTime - now < fiveMinutes;
};

// Get API base URL for fetch calls (native fetch doesn't use request baseURL)
const getApiBaseUrl = (): string => {
  // In development, use relative path (proxy will handle it)
  if (process.env.NODE_ENV === 'development') {
    return '';
  }
  // In production, use the configured API URL
  return process.env.REACT_APP_API_URL || 'https://aimerpay-api.aimerhq.com';
};

// Refresh the access token
const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = localStorage.getItem('aimerpay_refresh_token');
  if (!refreshToken) return null;

  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      throw new Error('Token refresh failed');
    }

    const result = await response.json();
    if (result.success && result.data) {
      localStorage.setItem('aimerpay_token', result.data.accessToken);
      localStorage.setItem('aimerpay_refresh_token', result.data.refreshToken);
      localStorage.setItem('aimerpay_token_expires', result.data.expiresAt);
      return result.data.accessToken;
    }
    throw new Error('Token refresh failed');
  } catch {
    // Clear tokens and redirect to login
    localStorage.removeItem('aimerpay_token');
    localStorage.removeItem('aimerpay_refresh_token');
    localStorage.removeItem('aimerpay_token_expires');
    return null;
  }
};

// Redirect to login preserving the current location so the user can return
// after signing back in (the login page consumes ?redirect= with a safe-list).
const redirectToLogin = () => {
  const { pathname, search } = window.location;
  if (pathname === '/user/login') return;
  history.push(`/user/login?redirect=${encodeURIComponent(pathname + search)}`);
};

// Deduplicate concurrent 401s: several in-flight requests can fail at once,
// but the user should get a single message and a single redirect.
let handlingSessionExpired = false;

const handleSessionExpired = () => {
  if (handlingSessionExpired) return;
  handlingSessionExpired = true;
  clearAuthState();
  message.error(
    getIntl().formatMessage({
      id: 'pages.login.sessionExpired',
      defaultMessage: 'Your session has expired. Please sign in again.',
    }),
  );
  redirectToLogin();
  setTimeout(() => {
    handlingSessionExpired = false;
  }, 1000);
};

// Auth whitelist - paths that don't need authentication
const authWhitelist = [
  '/api/auth/login',
  '/api/auth/refresh',
  '/api/userInvitation/verify',
  '/api/userInvitation/accept',
  '/api/auth/reset-password',
  '/api/auth/forgot-password',
];

// Check if URL is in auth whitelist
const isAuthWhitelisted = (url: string): boolean => {
  return authWhitelist.some((path) => url.includes(path));
};

/**
 * @name Error handling configuration
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  errorConfig: {
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, message, showType } =
        res as unknown as ResponseStructure;
      if (!success) {
        const msg = errorMessage || message || 'Request failed';
        const error: any = new Error(msg);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage: msg, showType, data };
        throw error;
      }
    },
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;

      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        if (error.response.status === 401) {
          // Token invalid or revoked server-side (the proactive refresh only
          // covers local expiry) — clear the session and return to login.
          handleSessionExpired();
        } else {
          // Backend business errors are returned as 4xx + ApiResponse{ message };
          // prefer that message over a bare status code. Fall back to ProblemDetails
          // (detail/title) and finally the raw status for non-standard bodies.
          const body = error.response.data;
          const backendMessage =
            body?.errorMessage || body?.message || body?.detail || body?.title;
          message.error(
            backendMessage || `Response status: ${error.response.status}`,
          );
        }
      } else if (error.request) {
        message.error('No response from server. Please retry.');
      } else {
        message.error('Request error, please retry.');
      }
    },
  },

  // Request interceptors
  requestInterceptors: [
    async (config: RequestOptions) => {
      const url = config.url || '';

      // Skip auth for whitelisted URLs
      if (isAuthWhitelisted(url)) {
        return config;
      }

      let token = localStorage.getItem('aimerpay_token');

      // Check if token is expiring soon and refresh it
      if (token && isTokenExpiringSoon()) {
        if (!isRefreshing) {
          isRefreshing = true;
          const newToken = await refreshAccessToken();
          isRefreshing = false;

          if (newToken) {
            token = newToken;
            onTokenRefreshed(newToken);
          } else {
            // Refresh failed, redirect to login
            redirectToLogin();
            return config;
          }
        } else {
          // Wait for the ongoing refresh to complete
          token = await new Promise<string>((resolve) => {
            subscribeTokenRefresh((newToken) => {
              resolve(newToken);
            });
          });
        }
      }

      // Add Authorization header
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };
      }

      // SuperAdmin acting-as a MerchantOwner: forward the impersonated owner's
      // userId so the backend writes ownership (UserPartner.UserId,
      // CreatedByUserId, ...) under the owner instead of the SuperAdmin.
      const actingAsRaw = sessionStorage.getItem(ACTING_AS_STORAGE_KEY);
      if (actingAsRaw) {
        try {
          const owner = JSON.parse(actingAsRaw);
          if (owner?.userId != null) {
            config.headers = {
              ...config.headers,
              'X-Acting-As-UserId': String(owner.userId),
            };
          }
        } catch {
          // ignore parse errors — bad sessionStorage value should not block requests
        }
      }

      return config;
    },
  ],

  // Response interceptors
  responseInterceptors: [
    async (response) => {
      return response;
    },
  ],
};
